import { useEffect, useRef, useState } from 'react';
import { svProject, svSample, svDist } from '../lib/svgeo';
import { resolvePt, PIPE_TYPES } from '../lib/model';
import { fitColor } from './networkLayers';
import { LOG_COLORS, loggerState } from '../hooks/useLoggers';

const segs = (pr) => pr.reduce((acc, p) => { if (!p) acc.push([]); else acc[acc.length - 1].push(p); return acc; }, [[]]).filter((s) => s.length > 1);
const fmt = (d) => `${d.toFixed(1).replace('.', ',')} m`;

// AR overlay: own pipes/objects within 50 m projected into the street view image
export default function SvOverlay({ t, cam, heading, pipes, fittings, loggers, onSel }) {
  const ref = useRef(null);
  const [sz, setSz] = useState({ w: 0, h: 0 });
  useEffect(() => {
    const ro = new ResizeObserver(([e]) => setSz({ w: e.contentRect.width, h: e.contentRect.height }));
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);
  const { w: W, h: H } = sz;
  const P = (p) => (W ? svProject(cam, heading, p, W, H) : null);
  const lines = pipes.flatMap((pp) => {
    const pts = pp.points.map((x) => resolvePt(x, fittings)).filter(Boolean);
    return segs(svSample(cam, pts).map((p) => (p ? P(p) : null))).map((s, k) => ({ id: pp.id, k, color: pp.color || PIPE_TYPES[pp.type] || PIPE_TYPES.other, dash: pp.private, s }));
  });
  const syms = [
    ...fittings.map((f) => ({ id: f.id, kind: 'fitting', ll: [f.lat, f.lon], sym: f.sym || t['fab_' + f.type] || '?', name: f.type === 'leak' ? t.fit_leak : f.name, color: fitColor(f) })),
    ...loggers.map((l) => ({ id: l.id, kind: 'logger', ll: [l.lat, l.lon], sym: 'GL', name: l.name, color: LOG_COLORS[loggerState(l)] })),
  ].filter((o) => svDist(cam, o.ll) <= 50).map((o) => ({ ...o, p: P(o.ll) })).filter((o) => o.p).sort((a, b) => b.p.d - a.p.d);
  return (
    <div className="sv-ar" ref={ref} data-testid="streetview-ar-overlay">
      <svg width={W} height={H} className="sv-ar-svg">
        {lines.map((l) => (
          <g key={`${l.id}-${l.k}`} data-testid={`sv-ar-pipe-${l.id}`} onClick={() => onSel({ kind: 'pipe', id: l.id })}>
            <polyline points={l.s.map((p) => `${p.x},${p.y}`).join(' ')} fill="none" stroke="#000" strokeOpacity="0.45" strokeWidth="8" strokeLinecap="round" />
            <polyline points={l.s.map((p) => `${p.x},${p.y}`).join(' ')} fill="none" stroke={l.color} strokeWidth="4.5" strokeLinecap="round" strokeDasharray={l.dash ? '10 8' : undefined} />
          </g>
        ))}
      </svg>
      {syms.map((o) => (
        <button key={o.id} className="sv-ar-sym" data-testid={`sv-ar-obj-${o.id}`} style={{ left: o.p.x, top: Math.min(Math.max(o.p.y, 28), H - 12), '--c': o.color }} onClick={() => onSel({ kind: o.kind, id: o.id })}>
          <b>[{o.sym}]</b> {o.name} ({fmt(o.p.d)})
        </button>
      ))}
      <span className="sv-ar-count" data-testid="streetview-ar-count">AR · {syms.length} {t.svArObjects} · 50 m</span>
    </div>
  );
}
